import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { startOfWeek, addDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Users, Clock, Syringe, TrendingUp, CheckCircle2, FileText, AlertTriangle, ShieldCheck, ShieldAlert, CalendarDays } from 'lucide-react';
import { useEmployees, useCoverageRange, useSchedules, useAppointments, useShuttleData, useAbsences, usePharmacySettings } from '@/api/resources';
import type { DayCoverage, HalfDayCoverage } from '@/api/resources';
import { useCurrentUser } from '@/api/auth';
import { Chip } from '@/components/ui/Chip';
import { absenceMeta } from '@/lib/absenceTypes';
import { weekDayCount } from '@/lib/schedule';

/**
 * `Absence.startDate` / `endDate` arrivent en ISO complet (« 2026-08-12T00:00:00.000Z »),
 * pas en « yyyy-MM-dd ». On ne garde que la partie date : concaténer 'T00:00:00' sur
 * une chaîne déjà horodatée donne une Date invalide, et date-fns lève sur `format()`.
 */
const dayPart = (iso: string): string => iso.slice(0, 10);

/** Durée d'un créneau « HH:mm » → heures décimales. */
function slotHours(start?: string | null, end?: string | null): number {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return eh + em / 60 - (sh + sm / 60);
}

export default function DashboardPage() {
  const { data: user } = useCurrentUser();
  const { data: employees, isLoading: loadingEmp } = useEmployees();

  const firstName = user?.email?.split('@')[0] ?? '';
  const empCount = employees?.length ?? 0;
  /**
   * Officine qui vient d'être créée : aucun salarié.
   *
   * C'est le seul signal fiable. Les horaires et les effectifs requis ont des
   * valeurs par défaut, mais on ne peut RIEN planifier sans personne — et c'est
   * l'absence de salariés qui fait virer au rouge tout le tableau de bord.
   */
  const isNewPharmacy = !loadingEmp && empCount === 0;
  const tempCount = employees?.filter((e) => e.contractType === 'REMPLACANT' || e.contractType === 'INTERIM').length ?? 0;

  // --- Semaine en cours (lun→sam, comme le planning) ---
  // Le dimanche entre dans la semaine si l'officine ouvre ce jour-là.
  const { data: pharmacy } = usePharmacySettings();
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weekFrom = format(weekStart, 'yyyy-MM-dd');
  const weekTo = format(addDays(weekStart, weekDayCount(pharmacy?.openingHours) - 1), 'yyyy-MM-dd');
  const { data: schedules, isLoading: loadingSch } = useSchedules(weekFrom, weekTo);

  const plannedHours = useMemo(() => {
    if (!schedules) return null;
    const total = schedules
      .filter((s) => s.status === 'WORK')
      .reduce(
        (acc, s) =>
          acc + slotHours(s.morningStart, s.morningEnd) + slotHours(s.afternoonStart, s.afternoonEnd),
        0,
      );
    return Math.round(total);
  }, [schedules]);

  // --- RDV des 7 prochains jours (annulés exclus) ---
  const today = new Date();
  const apptFrom = format(today, 'yyyy-MM-dd');
  const apptTo = format(addDays(today, 7), 'yyyy-MM-dd');
  const { data: appointments, isLoading: loadingAppt } = useAppointments(apptFrom, apptTo);
  const upcomingAppts = appointments?.filter((a) => a.status !== 'CANCELLED').length ?? null;

  // --- Heures supp du mois en cours (même calcul que la fiche navette : balance = HS + report) ---
  const { data: shuttle, isLoading: loadingShuttle } = useShuttleData(
    today.getFullYear(),
    today.getMonth() + 1,
  );
  const overtime = useMemo(() => {
    if (!shuttle) return null;
    const total = shuttle.lines.reduce((acc, l) => acc + l.totalOvertime + l.carriedIn, 0);
    return Math.round(total * 10) / 10;
  }, [shuttle]);

  return (
    <div className="p-8 space-y-6 max-w-[1400px] overflow-auto">
      <div className="grid grid-cols-[1fr_auto] gap-6 items-start">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-1.5 font-medium">
            Bonjour {firstName || 'Titulaire'}
          </div>
          <h2 className="text-[28px] font-semibold tracking-tight text-slate-900 leading-tight">
            {isNewPharmacy ? (
              <>Bienvenue. <span className="text-primary">Configurons votre officine.</span></>
            ) : (
              <>Voici votre semaine <span className="text-primary">en un coup d'œil.</span></>
            )}
          </h2>
        </div>
        {!isNewPharmacy && (
          <div className="flex gap-2">
            <Link to="/planning" className="btn-default">Voir le planning</Link>
            <Link to="/shuttle" className="btn-primary">
              <FileText size={14} /> Fiche navette
            </Link>
          </div>
        )}
      </div>

      {/* Pharmacie neuve : on GUIDE, on n'alarme pas.
          Sans salarié, sans horaires et sans effectifs requis, tous les
          indicateurs virent au rouge — la couverture tombe à 0 %, chaque
          demi-journée est signalée découverte. Ces alertes sont exactes mais
          absurdes : elles reprochent à quelqu'un de n'avoir pas encore fait ce
          qu'il vient à peine de commencer. On les remplace par les trois gestes
          qui, une fois faits, les feront disparaître d'elles-mêmes. */}
      {isNewPharmacy && <FirstStepsPanel />}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Salariés actifs" value={loadingEmp ? '…' : String(empCount)} sub={tempCount > 0 ? `dont ${tempCount} temporaires` : 'CDI / CDD'} icon={Users} />
        <StatCard
          label="Heures planifiées"
          value={loadingSch ? '…' : plannedHours === null ? '—' : `${plannedHours}h`}
          sub="cette semaine"
          icon={Clock}
        />
        <StatCard
          label="RDV à venir"
          value={loadingAppt ? '…' : upcomingAppts === null ? '—' : String(upcomingAppts)}
          sub="7 prochains jours"
          icon={Syringe}
        />
        <StatCard
          label={overtime !== null && overtime < 0 ? 'Heures dues' : 'Heures supp'}
          value={loadingShuttle ? '…' : overtime === null ? '—' : `${overtime > 0 ? '+' : ''}${overtime}h`}
          sub="ce mois, tous salariés"
          icon={TrendingUp}
        />
      </div>

      {/* Ni couverture ni échéances sur une pharmacie neuve : il n'y a rien à
          couvrir, et un panneau vide ou rouge n'apprend rien. */}
      {!isNewPharmacy && <CoveragePanel />}
      {!isNewPharmacy && <UpcomingPanel />}

      {/* Liens rapides */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { to: '/employees', label: 'Salariés', icon: Users, sub: `${empCount} actifs` },
          { to: '/appointments', label: 'Rendez-vous métier', icon: Syringe, sub: 'Agenda' },
          { to: '/planning', label: 'Planning semaine', icon: CheckCircle2, sub: 'Grille hebdo' },
          { to: '/shuttle', label: 'Fiche navette', icon: FileText, sub: 'Mensuelle' },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              className="group bg-white border border-slate-200 rounded-xl p-4 hover:border-primary hover:shadow-sm transition-all"
            >
              <Icon size={16} className="text-slate-500 group-hover:text-primary mb-3" />
              <div className="text-[13px] font-medium text-slate-900">{item.label}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{item.sub}</div>
            </Link>
          );
        })}
      </div>

    </div>
  );
}

/**
 * Premiers pas d'une officine qui vient d'être créée.
 *
 * Les trois réglages sont dans l'ordre où ils comptent, et chacun est COCHÉ
 * quand il est fait — la liste se vide donc d'elle-même. C'est volontairement
 * un guide et non une alerte : à ce stade, rien n'est en panne, il reste
 * seulement des choses à saisir.
 */
function FirstStepsPanel() {
  const { data: pharmacy } = usePharmacySettings();
  const { data: employees } = useEmployees();

  const hasEmployees = (employees?.length ?? 0) > 0;
  // `usePharmacySettings` renvoie `any` : on type ce qu'on lit, ici et pas ailleurs.
  const hasOpeningHours = (
    (pharmacy?.openingHours ?? []) as { closed: boolean }[]
  ).some((h) => !h.closed);
  const hasStaffing = (pharmacy?.staffingRequirements ?? []).length > 0;

  const steps = [
    {
      done: hasEmployees,
      to: '/employees',
      title: 'Ajoutez votre équipe',
      text: 'Sans salariés, aucun planning ne peut être généré. Commencez par vous, puis vos préparateurs.',
      cta: 'Ajouter un salarié',
    },
    {
      done: hasOpeningHours,
      to: '/settings',
      title: 'Renseignez vos horaires d\'ouverture',
      text: 'Ils bornent tout le reste : les horaires proposés à la saisie, les journées qui débordent, le comblement.',
      cta: 'Définir les horaires',
    },
    {
      done: hasStaffing,
      to: '/settings',
      title: 'Définissez vos effectifs requis',
      text: 'Combien de personnes il vous faut, par mission et par demi-journée. C\'est ce qui rend la couverture lisible.',
      cta: 'Définir les effectifs',
    },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <h3 className="text-[15px] font-semibold text-slate-900">Vos premiers pas</h3>
      <div className="text-[12px] text-slate-500 mt-0.5 mb-5">
        Trois réglages, et votre officine est opérationnelle. Les indicateurs de cette page
        s'activeront au fur et à mesure.
      </div>

      <ol className="space-y-3">
        {steps.map((s, i) => (
          <li
            key={s.to + i}
            className={`flex items-start gap-3.5 rounded-lg border p-4 ${
              s.done ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200'
            }`}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-semibold ${
                s.done ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
              }`}
            >
              {s.done ? <CheckCircle2 size={14} strokeWidth={2.5} /> : i + 1}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-medium text-slate-900">{s.title}</div>
              <div className="text-[12px] text-slate-600 mt-0.5 leading-relaxed">{s.text}</div>
            </div>
            {!s.done && (
              <Link to={s.to} className="btn-primary text-xs flex-shrink-0 self-center">
                {s.cta}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function CoveragePanel() {
  const { data: pharmacy } = usePharmacySettings();
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weekEnd = addDays(weekStart, weekDayCount(pharmacy?.openingHours) - 1);
  const fromIso = format(weekStart, 'yyyy-MM-dd');
  const toIso = format(weekEnd, 'yyyy-MM-dd');
  const { data: coverage, isLoading } = useCoverageRange(fromIso, toIso);

  // Ne garder que les demi-journées ouvertes sous l'effectif requis.
  const atRisk = (coverage ?? []).filter((d) => d.worst === 'PARTIAL' || d.worst === 'UNCOVERED');

  // Score global : part des demi-journées OUVERTES qui sont couvertes.
  // Les demi-journées fermées (CLOSED) sont exclues — sinon un dimanche gonflerait le score.
  const score = (() => {
    if (!coverage) return null;
    const halves = coverage.flatMap((d) => [d.morning, d.afternoon]).filter((h) => h.status !== 'CLOSED');
    if (halves.length === 0) return null;
    const ok = halves.filter((h) => h.status === 'COVERED').length;
    return { pct: Math.round((ok / halves.length) * 100), ok, total: halves.length };
  })();

  const scoreTone =
    score === null ? 'text-slate-400'
      : score.pct >= 95 ? 'text-emerald-600'
      : score.pct >= 80 ? 'text-amber-600'
      : 'text-red-600';

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-1">
        {atRisk.length === 0 ? (
          <ShieldCheck size={16} className="text-emerald-600" />
        ) : (
          <ShieldAlert size={16} className="text-amber-600" />
        )}
        <h3 className="text-[14px] font-semibold text-slate-900">Couverture de la semaine</h3>
        <span className="text-[11px] text-slate-400">
          du {format(weekStart, 'd MMM', { locale: fr })} au {format(weekEnd, 'd MMM', { locale: fr })}
        </span>
        {/* Score global : un seul chiffre pour savoir si la semaine tient. */}
        <span className="ml-auto flex items-baseline gap-1.5" title={score ? `${score.ok} demi-journées couvertes sur ${score.total} ouvertes` : undefined}>
          <span className={`text-[22px] font-semibold tabular-nums leading-none ${scoreTone}`}>
            {isLoading ? '…' : score === null ? '—' : `${score.pct} %`}
          </span>
          <span className="text-[10.5px] text-slate-400">couvert</span>
        </span>
      </div>
      <div className="text-[11.5px] text-slate-500 mb-3">
        Effectifs réellement disponibles (après indispos et congés) comparés aux besoins configurés.
      </div>

      {isLoading ? (
        <div className="text-[12px] text-slate-500 py-2">Analyse des disponibilités…</div>
      ) : atRisk.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 text-[12px] text-emerald-800">
          Tous les effectifs requis sont couverts cette semaine.
        </div>
      ) : (
        <div className="space-y-2">
          {atRisk.map((d) => (
            <CoverageRiskRow key={d.date} day={d} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Ce qui arrive dans les 30 prochains jours : congés, formations, arrêts.
 * Demande du boss — l'info existait, mais il fallait ouvrir chaque fiche salarié pour la voir.
 */
function UpcomingPanel() {
  const { data: absences, isLoading } = useAbsences();

  const upcoming = useMemo(() => {
    if (!absences) return [];
    const today = format(new Date(), 'yyyy-MM-dd');
    const limit = format(addDays(new Date(), 30), 'yyyy-MM-dd');
    return absences
      .filter((a) => dayPart(a.endDate) >= today && dayPart(a.startDate) <= limit)
      .sort((a, b) => dayPart(a.startDate).localeCompare(dayPart(b.startDate)))
      .slice(0, 6);
  }, [absences]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <CalendarDays size={16} className="text-slate-500" />
        <h3 className="text-[14px] font-semibold text-slate-900">À venir</h3>
        <span className="text-[11px] text-slate-400">30 prochains jours</span>
        <Link to="/requests" className="ml-auto text-[11px] text-primary hover:underline">Voir les demandes</Link>
      </div>
      <div className="text-[11.5px] text-slate-500 mb-3">
        Congés, formations et absences déjà validés qui vont impacter le planning.
      </div>

      {isLoading ? (
        <div className="text-[12px] text-slate-500 py-2">Chargement…</div>
      ) : upcoming.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-[12px] text-slate-600">
          Aucune absence prévue sur les 30 prochains jours.
        </div>
      ) : (
        <div className="space-y-1.5">
          {upcoming.map((a) => {
            const meta = absenceMeta(a.type);
            const name = a.employee ? `${a.employee.firstName} ${a.employee.lastName}` : 'Salarié';
            const single = dayPart(a.startDate) === dayPart(a.endDate);
            const d = (iso: string) => format(new Date(dayPart(iso) + 'T00:00:00'), 'd MMM', { locale: fr });
            return (
              <div key={a.id} className="flex items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: meta.dot }} />
                <span className="text-[12.5px] font-medium text-slate-900">{name}</span>
                <span className="text-[11.5px] text-slate-500">{meta.label}</span>
                <span className="ml-auto text-[11.5px] text-slate-600 tabular-nums whitespace-nowrap">
                  {single ? `le ${d(a.startDate)}` : `du ${d(a.startDate)} au ${d(a.endDate)}`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CoverageRiskRow({ day }: { day: DayCoverage }) {
  const dateObj = new Date(day.date + 'T00:00:00');
  const label = format(dateObj, 'EEEE d MMM', { locale: fr });
  const critical = day.worst === 'UNCOVERED';

  const halfLabel = (h: HalfDayCoverage, name: string) =>
    h.alerts.length > 0 ? h.alerts.map((a) => `${name} : ${a.message}`) : [];
  const messages = [...halfLabel(day.morning, 'Matin'), ...halfLabel(day.afternoon, 'A-M')];

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${critical ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
      <div className="flex items-center justify-between">
        <div className="text-[12.5px] font-medium text-slate-900 capitalize">{label}</div>
        <Chip variant={critical ? 'slate' : 'default'} size="sm">
          {critical ? 'Sous le minimum' : 'Sous la cible'}
        </Chip>
      </div>
      {messages.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {messages.map((m, i) => (
            <li key={i} className={`text-[11px] ${critical ? 'text-red-700' : 'text-amber-800'}`}>• {m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
}: {
  label: string;
  value: string;
  sub: string;
  icon: typeof Users;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="p-1.5 rounded-lg bg-slate-100 text-slate-600">
          <Icon size={14} strokeWidth={2} />
        </div>
      </div>
      <div className="text-[26px] font-semibold tracking-tight tabular-nums text-slate-900 leading-none">
        {value}
      </div>
      <div className="text-[11px] text-slate-500 mt-1.5">{label}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>
    </div>
  );
}
